/**
 * Step 3 — Damage Scenarios.
 *
 * Read mode + CRUD; mirrors Step 2's dispatcher (spinner / table /
 * hero) and uses the useConfirm dialog for the plain delete path. When the
 * backend returns 409 with `HAS_DEPENDENCIES` (risk assessments
 * referencing the scenario via damage_id) the confirm dialog hands
 * off to CascadeDeleteDialog so the user can choose force vs cascade.
 */
import React, { useState } from 'react';
import { LoadingState, FailedState } from '../../../components/ui/LoadingState';

import { useConfirm } from '../../../components/ui/AlertDialog';
import { Icon } from '../../../components/ui/Icon';
import CascadeDeleteDialog from '../components/CascadeDeleteDialog';
import ScenarioTable from './step3-scenarios/ScenarioTable';
import ScenarioFormModal from './step3-scenarios/ScenarioFormModal';
import HierarchicalScenarioReview from './step3-scenarios/HierarchicalScenarioReview';
import ImpactMatrix from './step3-scenarios/ImpactMatrix';
import type { ChildScenarioView } from './step3-scenarios/ChildScenariosTable';
import type { Step3BodyProps } from './stepBodyProps';
import type { ScenarioRecord } from './step3-scenarios/scenarioFieldSchema';
import { exportScenariosToCsv, parseScenarioFile } from './step3-scenarios/scenarioCsv';
import { notify } from '../../../services/notificationBus';
import styles from './Step3DamageScenarios.module.css';

// LoadingState/FailedState are JSX primitives; cast for use from TSX
// (mirrors the ButtonAny convention across the step-components).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;
const FailedStateAny = FailedState as unknown as React.ComponentType<any>;

const CascadeDeleteDialogAny = CascadeDeleteDialog as unknown as React.ComponentType<any>;

interface DependentRecord {
  id?: string;
  type?: string;
  description?: string;
  [k: string]: unknown;
}

interface CascadeState {
  scenario: ScenarioRecord;
  dependents: DependentRecord[];
}

type ModalState =
  | null
  | { kind: 'add' }
  | { kind: 'edit'; scenario: ScenarioRecord };

export default function Step3DamageScenarios({
  status,
  editable,
  structured,
  crud,
  assetOptions,
  hierarchical,
  errorMessage,
}: Step3BodyProps) {
  const [modal, setModal] = useState<ModalState>(null);
  const [cascade, setCascade] = useState<CascadeState | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  if (status === 'failed') {
    return (
      <FailedStateAny
        variant="page"
        title="Damage scenario analysis failed"
        description={errorMessage ?? structured.error ?? 'The damage scenario agent encountered an error. Revise and retry.'}
      />
    );
  }

  const scenarios = (structured.data ?? []) as ScenarioRecord[];

  // Spinner only on initial load — once data is in hand, post-save
  // refetches stay invisible (table keeps prior rows + re-renders on
  // resolve). Mirrors Step 2's `noDataYet` gate.
  const noDataYet = scenarios.length === 0 && !structured.data;
  if (
    status === 'empty' ||
    status === 'generating' ||
    status === 'revising' ||
    (structured.loading && noDataYet)
  ) {
    return (
      <LoadingStateAny
        variant="page"
        title="Identifying damage scenarios"
        description="The damage scenario agent is deriving impact scenarios from the assets. This usually takes 1–2 minutes."
      />
    );
  }

  const handleSave = async (values: Record<string, unknown>) => {
    try {
      if (modal?.kind === 'edit') {
        await crud.update(modal.scenario.damage_id, values);
        notify({ type: 'success', message: `Scenario "${values.damage_id}" updated.` });
      } else {
        await crud.add(values);
        notify({ type: 'success', message: `Scenario "${values.damage_id}" added.` });
      }
      setModal(null);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save scenario';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save scenario');
    }
  };

  const handleExport = () => {
    // Export the current full visible scenario list. In hierarchical
    // mode this is just the Current TARA tab's rows (child rows are
    // inherited and live in their own session's export).
    exportScenariosToCsv(hierarchical.enabled ? scenarios.filter((s: any) => s?.source !== 'child_tara') : scenarios);
  };

  const handleImport = async (file: File) => {
    let records: ScenarioRecord[];
    try {
      records = await parseScenarioFile(file);
    } catch (e: any) {
      notify({
        type: 'error',
        title: 'Import failed',
        message: e?.message || `Could not parse ${file.name}`,
      });
      return;
    }
    if (records.length === 0) {
      notify({ type: 'warning', message: `${file.name} contained no scenario rows.` });
      return;
    }

    const existingIds = new Set(scenarios.map((s) => s.damage_id));
    let added = 0;
    let skipped = 0;
    let failed = 0;
    for (const record of records) {
      if (!record?.damage_id) {
        failed += 1;
        continue;
      }
      if (existingIds.has(record.damage_id)) {
        skipped += 1;
        continue;
      }
      try {
        await crud.add(record as Record<string, unknown>);
        existingIds.add(record.damage_id);
        added += 1;
      } catch {
        failed += 1;
      }
    }

    if (added > 0) structured.refetch();
    const parts: string[] = [];
    if (added) parts.push(`${added} added`);
    if (skipped) parts.push(`${skipped} skipped (duplicate id)`);
    if (failed) parts.push(`${failed} failed`);
    notify({
      type: failed && !added ? 'error' : 'success',
      title: failed && !added ? 'Import failed' : 'Import complete',
      message: parts.join(' · ') || 'No rows processed.',
    });
  };

  const handleDelete = async (target: ScenarioRecord) => {
    const ok = await confirm({
      title: 'Delete damage scenario?',
      description: `Delete scenario "${target.damage_id}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await crud.remove(target.damage_id);
      notify({ type: 'success', message: `Scenario "${target.damage_id}" deleted.` });
      structured.refetch();
    } catch (e: any) {
      // Backend returns 409 with `code === 'HAS_DEPENDENCIES'` when
      // risk assessments reference this scenario via damage_id. Swap
      // the plain confirm for the cascade dialog so the user can
      // choose between dropping the scenario alone (force) or dropping
      // the whole dependency chain (cascade).
      if (e?.code === 'HAS_DEPENDENCIES' && Array.isArray(e?.dependentRecords)) {
        setCascade({ scenario: target, dependents: e.dependentRecords as DependentRecord[] });
        return;
      }
      const msg = e?.message || 'Failed to delete scenario';
      notify({ type: 'error', title: 'Delete failed', message: msg });
    }
  };

  const handleCascadeAction = async (mode: 'force' | 'cascade') => {
    if (!cascade) return;
    const target = cascade.scenario;
    try {
      const result: any = await crud.remove(target.damage_id, { [mode]: true });
      if (mode === 'cascade') {
        const count = Array.isArray(result?.cascade_results) ? result.cascade_results.length : 0;
        notify({
          type: 'info',
          message: `Deleted "${target.damage_id}" and ${count} dependent record${count !== 1 ? 's' : ''}.`,
        });
      } else {
        notify({
          type: 'warning',
          title: 'Deleted with broken references',
          message: `"${target.damage_id}" deleted; dependent records may now reference a missing scenario.`,
        });
      }
      setCascade(null);
      structured.refetch();
    } catch (e: any) {
      notify({
        type: 'error',
        title: 'Delete failed',
        message: e?.message || 'Failed to delete scenario',
      });
      setCascade(null);
    }
  };

  // Hierarchical split — backend tags inherited rows with `source:
  // 'child_tara'`. Matches legacy `HierarchicalStepReview` filter
  // (rows with missing source stay in the Current tab).
  const newScenarios = hierarchical.enabled
    ? scenarios.filter((s: any) => s?.source !== 'child_tara')
    : scenarios;
  const childScenarios: ChildScenarioView[] = hierarchical.enabled
    ? scenarios.filter((s: any) => s?.source === 'child_tara')
    : [];

  return (
    <div className={styles.bleed}>
      {hierarchical.enabled ? (
        <HierarchicalScenarioReview
          newScenarios={newScenarios}
          childScenarios={childScenarios}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(scenario) => setModal({ kind: 'edit', scenario })}
          onDelete={(scenario) => handleDelete(scenario)}
          onExport={handleExport}
          onImport={handleImport}
          modifications={hierarchical.modifications}
          onToggleRelevance={hierarchical.setRelevance}
          onJustificationChange={hierarchical.setJustification}
          childInputsDisabled={hierarchical.approving || !editable}
          onSaveChildModifications={hierarchical.onApprove}
        />
      ) : (
        <ScenarioTable
          scenarios={scenarios}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(scenario) => setModal({ kind: 'edit', scenario })}
          onDelete={(scenario) => handleDelete(scenario)}
          onExport={handleExport}
          onImport={handleImport}
        />
      )}
      {/* SFOP Impact Matrix — Story 15.B. Visible below the table when
          the step has output and at least one scenario exists. In
          hierarchical mode we show the matrix only for current-TARA
          scenarios (child TARAs have their own matrices in their own
          project workspace), matching the main table's scope. */}
      {scenarios.length > 0 && (
        <ImpactMatrix scenarios={hierarchical.enabled ? newScenarios : scenarios} />
      )}
      {modal && (
        <ScenarioFormModal
          mode={modal.kind}
          initialScenario={modal.kind === 'edit' ? modal.scenario : null}
          assetOptions={assetOptions}
          onSubmit={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      {confirmDialog}
      <CascadeDeleteDialogAny
        isOpen={!!cascade}
        recordType="Damage Scenario"
        recordId={cascade?.scenario.damage_id ?? ''}
        dependentRecords={cascade?.dependents ?? []}
        isDeleting={crud.saving}
        onClose={() => setCascade(null)}
        onDeleteOnly={() => handleCascadeAction('force')}
        onCascadeDelete={() => handleCascadeAction('cascade')}
      />
    </div>
  );
}
